import { useEffect, useState, useCallback, useContext } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { formatToSpace } from "../../utils";
import GameOverPrompt from "../../components/modals/GameOverPrompt";
import clsx from "clsx";
import { useTimer, useStopwatch } from "react-timer-hook";
import Injections from "../../components/injections/Injections";
import GenericGameOverPrompt from "../../components/modals/GenericGameOverPrompt";
import RecallAndMatchGameOverPrompt from "../../components/modals/RecallAndMatchGameOverPrompt";
import { AppLayout } from "../../layout/AppLayout";
import TriviaInjections from "../../components/injections/TriviaInjections";
import { Exit } from "../../components/svg/GeneralIcons";
import { useGetGameById } from "../../api/game/getGameById";
import { UserContext } from "../../components/context/UseContext";

function GameScreen({ play, stop, isPlaying }) {
  const { token } = useContext(UserContext);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const Id = searchParams.get("id");
  const gameId = searchParams.get("game_id");
  const name = searchParams.get("slug");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [score, setScore] = useState({ player: 0, computer: 0, tie: 0 });
  const [actionType, setActionType] = useState(null);
  const [status, setStatus] = useState(false);
  const [matches, setMatches] = useState(0);
  const [total, setTotal] = useState(8);
  const [difficulty, setDifficulty] = useState("easy");

  const onOpenModal = () => {
    setIsModalOpen(true);
    pause();
  };

  const { getGameById, data, loading } = useGetGameById();

  const onCloseModal = () => {
    // Add a delay before closing the modal
    setTimeout(() => {
      setIsModalOpen(false);
    }, 100);
  };

  let gameName = formatToSpace(location.search.slice(1));
  const time = new Date();
  time.setSeconds(time.getSeconds() + 60);

  const eightQueens = new Date();
  eightQueens.setSeconds(eightQueens.getSeconds() + 60 * 3);

  // Recall and match timer based on difficulty
  const getRecallAndMatchTime = useCallback(() => {
    const baseTime = new Date();
    switch (difficulty) {
      case "easy":
        baseTime.setSeconds(baseTime.getSeconds() + 50); // 50 seconds
        break;
      case "medium":
        baseTime.setSeconds(baseTime.getSeconds() + 60); // 1 minute
        break;
      case "hard":
        baseTime.setSeconds(baseTime.getSeconds() + 80); // 1 minute 20 seconds
        break;
      default:
        baseTime.setSeconds(baseTime.getSeconds() + 50); // default 50 seconds
    }
    return baseTime;
  }, [difficulty]);

  const isEightQueens = gameName === "eight queens";
  const isRecallAndMatch = name === "recall_and_match";
  const { seconds, minutes, isRunning, start, resume, pause, restart } =
    useTimer({
      expiryTimestamp: isEightQueens
        ? eightQueens
        : isRecallAndMatch
          ? getRecallAndMatchTime()
          : time,
      autoStart: gameName === "tic tac toe" || isEightQueens ? true : false,
      onExpire: () => {
        setActionType("expired");
        onOpenModal();
        pause();
      },
    });

  const { start: begin, pause: halt } = useStopwatch({ autoStart: true });

  useEffect(() => {
    if (gameId) getGameById(token, gameId);
  }, []);

  const retry = () => {
    const resetTime = new Date();
    resetTime.setSeconds(resetTime.getSeconds() + 60);
    if (formatToSpace(name) === "eight queens") {
      setStatus(false);
      setIsModalOpen(false);
      restart(eightQueens, true);
    } else if (formatToSpace(name) === "recall_and_match") {
      setStatus(false);
      setIsModalOpen(false);
      restart(getRecallAndMatchTime(), true);
    } else restart(resetTime);

    if (
      gameName !== "tic tac toe" &&
      gameName !== "eight queens" &&
      gameName !== "recall_and_match"
    ) {
      pause();
    }
    setIsReset(true);
  };

  const handleResume = () => {
    resume();
    begin();
  };

  const resetIsReset = (param) => {
    setIsReset(param);
  };

  const getScore = (scoreData) => {
    setScore(scoreData);
  };

  const getStatus = (param) => {
    setStatus(param);
  };

  const getTotal = (data) => {
    setTotal(data);
  };

  const getMatches = (matchCount) => {
    setMatches(matchCount);
  };

  const getDifficulty = (difficultyLevel) => {
    setDifficulty(difficultyLevel);
  };

  const gameWon = () => {
    setStatus(true);
  };

  useEffect(() => {
    if (status === true) {
      setIsModalOpen(true);
      pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Update timer duration when difficulty changes for recall and match
  useEffect(() => {
    if (isRecallAndMatch && difficulty) {
      const newTime = getRecallAndMatchTime();

      // Always update the timer duration, but preserve running state
      if (isRunning) {
        restart(newTime, true); // true to maintain running state
      } else {
        restart(newTime, false); // false to not auto-start
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [difficulty]); // Only depend on difficulty to avoid double triggers

  return (
    <AppLayout>
      <div
        style={{
          backgroundImage:
            name === "spin_the_wheels"
              ? `url('../../../image/spin_bg.png')`
              : "none",
        }}
        className="h-full bg-black/95 pb-16 bg-no-repeat bg-cover bg-top"
      >
        {name === "recall_and_match" && isModalOpen && actionType !== "end" ? (
          <RecallAndMatchGameOverPrompt
            actionType={actionType}
            closeModal={onCloseModal}
            retry={retry}
            status={status}
            handleResume={handleResume}
            difficulty={difficulty}
            totalMatches={matches}
            totalImages={total}
            id={Id}
            gameId={gameId}
          />
        ) : (
          (name !== "recall_and_match" || actionType === "end") && (
            <>
              {formatToSpace(name) === "eight_queens" ? (
                <GenericGameOverPrompt
                  isOpen={isModalOpen}
                  actionType={actionType}
                  closeModal={onCloseModal}
                  score={matches}
                  retry={retry}
                  status={status}
                  handleResume={handleResume}
                  color={data?.brands?.[0]?.primary_color}
                />
              ) : (
                <GameOverPrompt
                  isOpen={isModalOpen}
                  actionType={actionType}
                  closeModal={onCloseModal}
                  score={score}
                  retry={retry}
                  handleResume={handleResume}
                  color={data?.brands?.[0]?.primary_color}
                />
              )}
            </>
          )
        )}
        <div className="pt-10 px-9 flex items-center justify-between">
          {loading ? (
            <div className="h-7 w-32 bg-gray-600 rounded animate-pulse"></div>
          ) : (
            <h3
              className="text-2xl font-extrabold text-left text-white"
              style={{
                textShadow: `3px 3px 0 ${data?.brands?.[0]?.primary_color || "#003399"}`,
              }}
            >
              {data?.game?.name}
            </h3>
          )}
          {name !== "spin_the_wheels" && (
            <div className={clsx()}>
              <span
                onClick={() => {
                  setActionType("end");
                  pause();
                  halt();

                  // Reset timer for recall and match when ending game
                  if (isRecallAndMatch) {
                    const resetTime = getRecallAndMatchTime();
                    restart(resetTime, false);
                  }

                  onOpenModal();
                }}
                className="cursor-pointer"
              >
                <Exit className={"text-white"} />
              </span>
            </div>
          )}
        </div>
        <div
          className={clsx(
            name === "spin_the_wheels"
              ? "relative"
              : "relative max-h-[90vh] min-h-[80vh]",
          )}
        >
          {name?.toLowerCase()?.includes("trivia") ? (
            <TriviaInjections
              type={name}
              isReset={isReset}
              start={start}
              resetIsReset={resetIsReset}
              getStatus={getStatus}
              getScore={getScore}
              getTotal={getTotal}
              handleResume={handleResume}
              gameWon={gameWon}
              id={Id}
              color={data?.brands?.[0]?.primary_color}
            />
          ) : (
            <Injections
              type={name}
              isReset={isReset}
              start={start}
              resetIsReset={resetIsReset}
              getStatus={getStatus}
              getScore={getScore}
              getTotal={getTotal}
              getMatches={getMatches}
              handleResume={handleResume}
              gameWon={gameWon}
              getDifficulty={getDifficulty}
              timerSeconds={seconds}
              timerMinutes={minutes}
              timerRunning={isRunning}
            />
          )}
        </div>
      </div>
    </AppLayout>
  );
}

export default GameScreen;
